import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Send, 
  Brain, 
  Sparkles, 
  Users, 
  Lock, 
  Globe, 
  UserCheck, 
  Shield,
  X
} from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';
import { useUI } from '../../hooks/useUI';
import { useAuth } from '../../hooks/useAuth';
import { SCREENS } from '../../utils/constants';

/**
 * Text Creation Screen - Futuristic iPhone 17 iOS 26 Design
 * Dopamine-optimized interface for sharing thoughts
 */
const TextCreationScreen = () => {
  const { t } = useLanguage();
  const { navigateToScreen, screenParams, addFeedItem } = useUI();
  const { user } = useAuth();
  // State management
  const [content, setContent] = useState('');
  const [selectedPrivacy, setSelectedPrivacy] = useState('circle');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  // Refs
  const textareaRef = useRef(null);
  const hapticRef = useRef(null);

  // Privacy options matching IdentityFeed
  const privacyOptions = [
    {
      id: 'circle',
      title: t('privacy.circle', 'Circle'),
      description: t('privacy.circleDesc', 'Tüm circle üyelerine görünür'),
      icon: Users,
      gradient: 'from-green-500 to-emerald-600',
      emoji: '👥'
    },
    {
      id: 'close',
      title: t('privacy.closeCircle', 'Yakın Circle'),
      description: t('privacy.closeDesc', 'Sadece yakın arkadaşlarına görünür'),
      icon: UserCheck,
      gradient: 'from-amber-500 to-orange-600',
      emoji: '❤️'
    },
    {
      id: 'selected',
      title: t('privacy.selected', 'Seçili Kişiler'),
      description: t('privacy.selectedDesc', 'Belirlediğin kişilere görünür'),
      icon: Shield,
      gradient: 'from-blue-500 to-cyan-600',
      emoji: '👤'
    },
    {
      id: 'vault',
      title: t('privacy.vault', 'Dijital Kasa'),
      description: t('privacy.vaultDesc', 'Sadece sana görünür, şifrelenmiş'),
      icon: Lock,
      gradient: 'from-purple-600 to-violet-700',
      emoji: '🔒'
    }
  ];

  // Get content type from navigation params
  useEffect(() => {
    if (screenParams?.contentType) {
      console.log('Text creation screen opened for:', screenParams.contentType);
    }
  }, [screenParams]);

  // Auto-focus textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Update character count
  useEffect(() => {
    setCharCount(content.length);
  }, [content]);

  // Haptic feedback
  const triggerHaptic = useCallback((type = 'soft') => {
    if (window.navigator && window.navigator.vibrate) {
      const patterns = {
        soft: [10],
        medium: [20],
        strong: [30]
      };
      window.navigator.vibrate(patterns[type] || patterns.soft);
    }
  }, []);

  // Handle back navigation
  const handleBack = useCallback(() => {
    triggerHaptic('soft');
    navigateToScreen(SCREENS.IDENTITY_CIRCLE);
  }, [navigateToScreen, triggerHaptic]);

  // Handle privacy selection
  const handlePrivacySelect = useCallback((privacy) => {
    setSelectedPrivacy(privacy);
    setShowPrivacyModal(false);
    triggerHaptic('soft');
  }, [triggerHaptic]);
  // Handle post
  const handlePost = useCallback(async () => {
    if (!content.trim() || isPosting) return;

    setIsPosting(true);
    triggerHaptic('medium');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));        // Create new feed item
      const newPost = {
        type: 'thought',
        user: {
          name: user?.name || 'You',
          profilePhoto: user?.profilePhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face&auto=format&q=80',
          avatar: user?.name?.charAt(0) || 'Y',
          verified: user?.verified || false
        },
        content: content.trim(),
        timestamp: new Date(),
        privacy: selectedPrivacy,
        likes: 0,
        comments: [],
        shares: 0,
        tags: []
      };
      
      // Add to feed
      addFeedItem(newPost);      
      console.log('Posting thought:', {
        content: content.trim(),
        privacy: selectedPrivacy,
        timestamp: Date.now()
      });

      // Navigate back with success animation
      navigateToScreen(SCREENS.IDENTITY_CIRCLE);
    } catch (error) {
      console.error('Failed to post:', error);
    } finally {
      setIsPosting(false);
    }
  }, [content, selectedPrivacy, isPosting, navigateToScreen, triggerHaptic, addFeedItem, user]);

  // Get selected privacy option
  const selectedPrivacyOption = privacyOptions.find(opt => opt.id === selectedPrivacy);
  const SelectedPrivacyIcon = selectedPrivacyOption?.icon;

  // Check if can post
  const canPost = content.trim().length > 0 && content.length <= 500 && !isPosting;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            background: [
              'radial-gradient(circle at 20% 50%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 20%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 40% 80%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)'
            ]
          }}
          transition={{ duration: 8, repeat: Infinity, repeatType: 'reverse' }}
        />
        
        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-purple-400/30 rounded-full"
            animate={{
              x: [Math.random() * window.innerWidth, Math.random() * window.innerWidth],
              y: [Math.random() * window.innerHeight, Math.random() * window.innerHeight],
            }}
            transition={{
              duration: 10 + Math.random() * 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            style={{
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%'
            }}
          />
        ))}
      </div>

      {/* Header */}
      <motion.div 
        className="relative z-10 flex items-center justify-between p-6 backdrop-blur-xl bg-slate-800/40 border-b border-white/10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center space-x-4">
          <motion.button
            className="w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleBack}
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                {t('create.shareThought', 'Düşünceni Paylaş')}
              </h1>
              <p className="text-sm text-white/60">
                {t('create.expressYourself', 'Kendini ifade et')}
              </p>
            </div>
          </div>
        </div>

        <motion.button
          className={`px-6 py-3 rounded-2xl font-semibold transition-all flex items-center space-x-2 ${
            canPost
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg hover:shadow-purple-500/25'
              : 'bg-white/10 text-white/50 cursor-not-allowed'
          }`}
          whileHover={canPost ? { scale: 1.05, y: -2 } : {}}
          whileTap={canPost ? { scale: 0.95 } : {}}
          onClick={handlePost}
          disabled={!canPost}
        >
          {isPosting ? (
            <>
              <motion.div
                className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              <span>{t('common.posting', 'Paylaşılıyor...')}</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>{t('common.share', 'Paylaş')}</span>
            </>
          )}
        </motion.button>
      </motion.div>

      {/* Content Area */}
      <div className="relative z-10 p-6 space-y-6">
        {/* Main text input */}
        <motion.div
          className="bg-gradient-to-br from-slate-800/60 via-slate-700/40 to-slate-900/60 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('create.thoughtPlaceholder', 'Aklından geçenleri paylaş...')}
            className="w-full h-64 bg-transparent text-white text-lg leading-relaxed placeholder-white/40 resize-none focus:outline-none"
            maxLength={500}
          />

          {/* Character count */}
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-white/60 text-sm">
                {t('create.expressFreely', 'Özgürce ifade et')}
              </span>
            </div>
            <div className={`text-sm font-medium ${
              charCount > 450 ? 'text-red-400' : charCount > 400 ? 'text-yellow-400' : 'text-white/60'
            }`}>
              {charCount}/500
            </div>
          </div>        </motion.div>

        {/* Privacy selection */}
        <motion.div
          className="bg-gradient-to-br from-slate-800/60 via-slate-700/40 to-slate-900/60 backdrop-blur-xl rounded-2xl border border-white/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <button
            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors rounded-2xl"
            onClick={() => setShowPrivacyModal(true)}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 bg-gradient-to-r ${selectedPrivacyOption?.gradient} rounded-xl flex items-center justify-center`}>
                <SelectedPrivacyIcon className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-white font-semibold">{selectedPrivacyOption?.title}</h3>
                <p className="text-white/60 text-sm">{selectedPrivacyOption?.description}</p>
              </div>
            </div>
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center"
            >
              <span className="text-white/60">›</span>
            </motion.div>
          </button>
        </motion.div>
      </div>

      {/* Privacy Selection Modal */}
      <AnimatePresence>
        {showPrivacyModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPrivacyModal(false)}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/70 backdrop-blur-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            
            {/* Modal content */}
            <motion.div
              className="relative w-full max-w-lg bg-gradient-to-br from-slate-800/95 via-slate-700/90 to-slate-900/95 backdrop-blur-3xl rounded-3xl p-8 border border-white/10 shadow-2xl"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                  <Shield className="w-8 h-8 text-white" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  {t('privacy.title', 'Gizlilik Ayarları')}
                </h2>
                <p className="text-white/70">
                  {t('privacy.subtitle', 'İçeriğini kimler görebilsin?')}
                </p>
              </div>

              <div className="space-y-4">
                {privacyOptions.map((option) => {
                  const IconComponent = option.icon;
                  const isSelected = selectedPrivacy === option.id;
                  return (
                    <motion.button
                      key={option.id}
                      className={`w-full p-5 rounded-2xl bg-gradient-to-r ${option.gradient} flex items-center space-x-4 text-white shadow-xl border-2 transition-all ${
                        isSelected ? 'border-white/40 scale-105' : 'border-white/10'
                      }`}
                      whileHover={{ scale: isSelected ? 1.05 : 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handlePrivacySelect(option.id)}
                    >
                      <div className="w-12 h-12 bg-white/25 rounded-xl flex items-center justify-center">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-bold text-lg">{option.title}</h3>
                        <p className="text-sm text-white/90 mt-1">{option.description}</p>
                      </div>
                      <motion.div
                        className={`w-6 h-6 border-2 border-white/50 rounded-full flex items-center justify-center ${
                          isSelected ? 'bg-white/20' : ''
                        }`}
                        whileHover={{ scale: 1.2 }}
                      >
                        {isSelected && (
                          <motion.div
                            className="w-3 h-3 bg-white rounded-full"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          />
                        )}
                      </motion.div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Close button */}
              <motion.button
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                onClick={() => setShowPrivacyModal(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TextCreationScreen;
